import React from 'react';
import LabelAvatarStack from '../../../components/shared/LabelAvatarStack';
import EmptyState from '../../../components/shared/EmptyState';

function RevenueSongTable({ songs, currentPage, totalSongs, songsPerPage, onPageChange, onSelectSong, isLoading }) {
  const totalPages = Math.ceil(totalSongs / songsPerPage) || 1;

  // Generate pages array: [1, 2, 3, 4, 5, 6, ..., 16]
  const pageNumbers = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
  } else {
    for (let i = 1; i <= 6; i++) {
      pageNumbers.push(i);
    }
    pageNumbers.push('ellipsis');
    pageNumbers.push(totalPages);
  }

  const startIdx = totalSongs === 0 ? 0 : (currentPage - 1) * songsPerPage + 1;
  const endIdx = Math.min(currentPage * songsPerPage, totalSongs);

  return (
    <div className="song-table-container">
      {/* Table Headers */}
      <div
        className="song-table-grid song-table-header"
        style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1.3fr 0.7fr 1fr 1fr 1fr 1fr',
          gap: '12px',
          padding: '14px 20px',
          backgroundColor: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
          fontSize: '12px',
          fontWeight: '600',
          color: '#64748b'
        }}
      >
        <div>Song Name</div>
        <div>Artist</div>
        <div className="center-header">Ownership</div>
        <div style={{ textAlign: 'right' }}>Income</div>
        <div style={{ textAlign: 'right' }}>Distributor (30%)</div>
        <div style={{ textAlign: 'right' }}>Earning</div>
        <div style={{ textAlign: 'right' }}>Out Going</div>
      </div>

      {/* Table Body Rows */}
      <div className="song-table-body">
        {songs.length === 0 ? (
          <EmptyState
            hasSearch={true}
            title="No revenue records found"
            subtitle="No song revenue data matches your criteria."
          />
        ) : (
          songs.map((song, index) => {
            const isFirstRow = index === 0;
            return (
              <div
                key={song.id || index}
                className={`song-table-grid song-row ${isFirstRow ? 'highlighted-row' : ''}`}
                onClick={() => onSelectSong && onSelectSong(song)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1.3fr 0.7fr 1fr 1fr 1fr 1fr',
                  gap: '12px',
                  alignItems: 'center',
                  padding: '14px 20px',
                  borderBottom: '1px solid #f1f5f9',
                  cursor: 'pointer'
                }}
              >
                {/* Song Name */}
                <div className="cell-song-name">
                  <div className="main-text" style={{ fontSize: '14px', fontWeight: '600', color: '#0f172a' }}>
                    {song.nameSinhala || song.name}
                  </div>
                  <div className="sub-text" style={{ fontSize: '12px', color: '#64748b' }}>
                    {song.name}
                  </div>
                </div>

                {/* Artist */}
                <div>
                  <div className="main-text" style={{ fontSize: '13px', fontWeight: '600', color: '#0f172a' }}>
                    {(song.artist || '').split(',').map(name => name.trim()).filter(Boolean).map(name => name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')).join(' / ')}
                  </div>
                </div>

                {/* Ownership */}
                <div className="cell-ownership center-text" style={{ textAlign: 'center', fontSize: '14px', fontWeight: '500', color: '#475569' }}>
                  {song.ownership !== undefined && song.ownership !== null
                    ? (typeof song.ownership === 'number'
                        ? `${song.ownership}%`
                        : (String(song.ownership).endsWith('%') ? song.ownership : `${song.ownership}%`))
                    : '100%'}
                </div>

                {/* Income */}
                <div style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a', textAlign: 'right' }}>
                  {song.income || song.totalRevenue || '$0.00'}
                </div>

                {/* Distributor (30%) */}
                <div style={{ fontSize: '14px', fontWeight: '700', color: '#d97706', textAlign: 'right' }}>
                  {song.distributor || '$0.00'}
                </div>

                {/* Earning */}
                <div style={{ fontSize: '14px', fontWeight: '700', color: '#166534', textAlign: 'right' }}>
                  {song.earning || '$0.00'}
                </div>

                {/* Out Going */}
                <div style={{ fontSize: '14px', fontWeight: '700', color: '#dc2626', textAlign: 'right' }}>
                  {song.outgoing || '$0.00'}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination Footer */}
      <div className="table-pagination-footer">
        <div className="pagination-info">
          {startIdx}-{endIdx} of {totalSongs}
        </div>
        
        <div className="pagination-controls">
          <button
            className="pagination-btn arrow-btn"
            disabled={currentPage === 1}
            onClick={() => onPageChange(1)}
            aria-label="First Page"
          >
            &lt;&lt;
          </button>
          
          <button
            className="pagination-btn arrow-btn"
            disabled={currentPage === 1}
            onClick={() => onPageChange(currentPage - 1)}
            aria-label="Previous Page"
          >
            &lt;
          </button>

          {pageNumbers.map((num, i) => {
            if (num === 'ellipsis') {
              return <span key={`ellipsis-${i}`} className="pagination-ellipsis">...</span>;
            }
            return (
              <button
                key={`page-${num}`}
                className={`pagination-btn num-btn ${currentPage === num ? 'active' : ''}`}
                onClick={() => onPageChange(num)}
              >
                {num}
              </button>
            );
          })}

          <button
            className="pagination-btn arrow-btn"
            disabled={currentPage === totalPages}
            onClick={() => onPageChange(currentPage + 1)}
            aria-label="Next Page"
          >
            &gt;
          </button>

          <button
            className="pagination-btn arrow-btn"
            disabled={currentPage === totalPages}
            onClick={() => onPageChange(totalPages)}
            aria-label="Last Page"
          >
            &gt;&gt;
          </button>
        </div>
      </div>
    </div>
  );
}

export default RevenueSongTable;
